// Faces UI on src/ui (HD-6 Raw footage): the naming control for the eye's person tags on the clip panel.
//   <FaceCard> one card per tag: description, usual position and counts, jump links that seek the player, a proposed name
//              (Confirm / Reject), the name select, Save / Clear, and after a write ONE persistent Saved or Cleared with a
//              one-level Undo.
// Choices (W-10), in order: the performers cast on THIS card's script (`cast`, the API's faceCast), the two crew identities
// (Director, Crew: never a performer), then a collapsed "Other performer" entry that opens the rest of the client's registry
// (`chars`). Every write is PUT / DELETE /api/footage/:id/faces/:tag, proposals POST /api/footage/:id/faces/:tag/proposal,
// with the same bodies and audit notes as before (the undo note text is an API value and is kept as it was).
// compact (HD-8, the Production page's Shoot rows): no jump links, since that dialog has no player to seek (as the
// retired FaceNamerLegacy compact card).
import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Play, Undo2 } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { clean } from '../shell/copy';

export const CREW_CHOICES = [{ value: 'crew:Director', label: 'Director' }, { value: 'crew:Crew', label: 'Crew' }];
export const fmtT = (t) => { const m = Math.floor(t / 60); return `${m}:${String(Math.floor(t - m * 60)).padStart(2, '0')}`; };
export const authoredLabel = (a) => !a ? 'unnamed' : a.kind === 'crew' ? (a.label || 'Crew') : a.character;
const bodyFor = (choice) => choice.startsWith('crew:') ? { crew: choice.slice(5) } : { characterId: choice };
/** The write that restores a prior stored value exactly: a character → PUT { characterId }, crew → PUT { crew }, none → DELETE. */
const restoreBodyFor = (prev) => !prev ? null : prev.kind === 'crew' ? { crew: prev.label } : { characterId: prev.characterId };

/**
 * Save / Clear / Undo state per tag: { status: 'saving' | 'saved' | 'error', cleared, prev, error }.
 * 'saved' is persistent (no timer) and carries `prev`, the stored value before the write, so Undo can restore it.
 */
export function useFaceSave(footageId, onFaces) {
  const [state, setState] = useState({});
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const set = useCallback((tag, v) => { if (mounted.current) setState(prev => ({ ...prev, [tag]: v })); }, []);
  const write = useCallback(async (tag, body, note) => {
    if (body === null) return api.delete(`/api/footage/${footageId}/faces/${tag}`, note ? { data: { note } } : undefined);
    return api.put(`/api/footage/${footageId}/faces/${tag}`, note ? { ...body, note } : body);
  }, [footageId]);
  const run = useCallback(async (tag, choice, clear = false, prev = null) => {
    if (!clear && !choice) return false;
    set(tag, { status: 'saving', cleared: clear, prev });
    try {
      const r = await write(tag, clear ? null : bodyFor(choice));
      onFaces?.(r.data.faces);
      set(tag, { status: 'saved', cleared: clear, prev: prev || null, undoable: true });
      return true;
    } catch (e) {
      set(tag, { status: 'error', cleared: clear, prev, error: e?.response?.data?.error || (clear ? 'The name was not cleared. Try again.' : 'The name was not saved. Try again.') });
      return false;
    }
  }, [write, onFaces, set]);
  const undo = useCallback(async (tag, current) => {
    const st = state[tag]; if (!st || st.status !== 'saved' || !st.undoable) return false;
    const prev = st.prev || null; const body = restoreBodyFor(prev);
    // The audit note is stored by the API; its wording is unchanged from before HD-6.
    const note = `undo — restored ${authoredLabel(prev)}${prev && prev.kind === 'crew' ? ' (crew)' : ''} (was ${authoredLabel(current)}${current && current.kind === 'crew' ? ' (crew)' : ''})`;
    set(tag, { status: 'saving', undoing: true, prev });
    try {
      const r = await write(tag, body, note);
      onFaces?.(r.data.faces);
      set(tag, undefined);
      return true;
    } catch (e) { set(tag, { status: 'error', prev, error: e?.response?.data?.error || 'The undo did not go through. Try again.' }); return false; }
  }, [state, write, onFaces, set]);
  const reset = useCallback(() => { if (mounted.current) setState({}); }, []);
  return { state, run, undo, reset };
}

/** The value of the collapsed "Other performer" entry: opens the full registry, never saved. */
export const OTHER_CHOICE = '__other';

function FaceChoiceOptions({ cast, chars, authored, otherOpen }) {
  const castList = cast || []; const castIds = new Set(castList.map(c => c.id));
  const others = (chars || []).filter(c => !castIds.has(c.id));
  const opt = (group, value, label) => <option key={`${group}:${value}`} value={value} data-testid="footage-face-option" data-group={group}>{label}</option>;
  return (<>
    <option value="">{authored ? `Keep ${authoredLabel(authored)}` : 'Who is this?'}</option>
    {castList.length > 0 && <optgroup label="Cast on this script">{castList.map(c => opt('cast', c.id, c.name))}</optgroup>}
    <optgroup label="Crew (never a performer)">{CREW_CHOICES.map(c => opt('crew', c.value, c.label))}</optgroup>
    {!otherOpen
      ? opt('other', OTHER_CHOICE, 'Other performer')
      : <optgroup label="Other performers (full registry)">{others.length ? others.map(c => opt('registry', c.id, c.name)) : <option disabled value="__none">No other characters in the registry</option>}</optgroup>}
  </>);
}

function NamedLine({ authored }) {
  if (!authored) return null;
  const when = authored.namedAt ? new Date(authored.namedAt).toLocaleDateString() : null;
  return (
    <span data-testid="footage-face-named" data-kind={authored.kind || 'character'} className="ui-meta">
      Named <strong className="ui-text-2">{clean(authoredLabel(authored))}</strong>{authored.kind === 'crew' ? ' (crew)' : ''}
      {authored.namedByName ? ` by ${clean(authored.namedByName)}` : ''}{when ? `, ${when}` : ''}{authored.note ? `, ${clean(authored.note)}` : ''}
    </span>
  );
}

const sourceLabel = s => s === 'exemplar' ? 'looks like a named reference' : s === 'correlation' ? 'lips move on their lines' : s;

// D-182: a proposed name, shown on an unnamed tag only and never counted as a name. Confirm promotes it to the name (the same
// role gate and audit as naming by hand); Reject drops it for good. Read-only viewers see it without the buttons.
function ProposalLine({ face: f, footageId, canName, onFaces }) {
  const [st, setSt] = useState(null);
  if (f.authored || !f.proposed) return null;
  const p = f.proposed; const pct = p.confidence != null ? `${Math.round(p.confidence * 100)}%` : '';
  const sources = (p.sources || []).map(sourceLabel).join(' and ');
  const act = async (action) => {
    setSt({ status: 'saving', action });
    try { const r = await api.post(`/api/footage/${footageId}/faces/${f.tag}/proposal`, { action }); onFaces?.(r.data.faces); setSt(null); }
    catch (e) { setSt({ status: 'error', error: e?.response?.data?.error || (action === 'confirm' ? 'The proposal was not confirmed. Try again.' : 'The proposal was not rejected. Try again.') }); }
  };
  const busy = st?.status === 'saving';
  return (
    <div data-testid="footage-face-proposal" data-tag={f.tag} data-character={p.character} data-low={p.lowConfidence ? 'true' : 'false'} className="ui-stack ui-stack--tight"
      title={`Proposed by first-contact provisioning: ${sources}${pct ? ` (${pct})` : ''}. Not a name until a person confirms it.`}>
      <span className="ui-dense ui-text-2">
        Proposed: <strong>{clean(p.character)}</strong>{sources ? `, ${sources}` : ''}{pct ? `, ${pct}` : ''}
      </span>
      {p.lowConfidence && <span data-testid="footage-face-proposal-low" className="ui-meta">Low confidence. Check the face before confirming.</span>}
      {p.sharedWith && p.sharedWith.length > 0 && (
        <span data-testid="footage-face-proposal-shared" className="ui-meta">Also proposed for {p.sharedWith.join(', ')}. The eye may have split one person.</span>
      )}
      {canName && (
        <span className="ui-cluster">
          <Button variant="secondary" data-testid="footage-face-proposal-confirm" disabled={busy} loading={busy && st.action === 'confirm'} onClick={() => act('confirm')}>Confirm</Button>
          <Button variant="quiet" data-testid="footage-face-proposal-reject" disabled={busy} loading={busy && st.action === 'reject'} onClick={() => act('reject')}>Reject</Button>
        </span>
      )}
      {st?.status === 'error' && <span data-testid="footage-face-proposal-error" role="alert" className="ui-field__error">{clean(st.error)}</span>}
    </div>
  );
}

export function FaceCard({ face: f, footageId, cast, chars, canName, onFaces, onSeek, saver, compact = false }) {
  const [choice, setChoice] = useState('');
  const [otherOpen, setOtherOpen] = useState(false);
  const own = useFaceSave(footageId, onFaces); const sv = saver || own;
  const st = sv.state[f.tag]; const saving = st?.status === 'saving';
  const save = async (clear) => { const ok = await sv.run(f.tag, choice, clear, f.authored || null); if (ok) setChoice(''); };
  const jumps = f.jumps || [];
  return (
    <div data-testid="footage-face-card" data-tag={f.tag} className="ui-region ui-region--pad ui-stack">
      <div className="ui-stack ui-stack--tight">
        <span className="ui-cluster">
          <span className="ui-tc">{f.tag}</span>
          <span data-testid="footage-face-description" className="ui-dense">
            {f.description ? clean(f.description) : <span className="ui-text-3">No description on the cast sheet</span>}
          </span>
        </span>
        <span data-testid="footage-face-position" className="ui-meta">
          Usually {f.position || 'unplaced'}{f.positionShare ? ` (${Math.round(f.positionShare * 100)}% of frames)` : ''}, present in {f.present} frame{f.present === 1 ? '' : 's'}, speaking in {f.speaking}
        </span>
      </div>
      {!compact && <span className="ui-cluster">
        {jumps.map(t => (
          <Button key={t} variant="quiet" icon={Play} data-testid="footage-face-jump" title="Seek the player to this moment" onClick={() => onSeek?.(t)}>
            <span className="ui-tc">{fmtT(t)}</span>
          </Button>
        ))}
        {!jumps.length && <span className="ui-meta">No sample frames</span>}
      </span>}
      <NamedLine authored={f.authored} />
      <ProposalLine face={f} footageId={footageId} canName={canName} onFaces={onFaces} />
      {st?.status === 'error' && <span data-testid="footage-face-error" role="alert" className="ui-field__error">{clean(st.error)}</span>}
      {canName ? (
        <div className="ui-stack ui-stack--tight">
          <span className="ui-select-wrap">
            <select className="ui-input ui-select" aria-label={`Name person ${f.tag}`} data-testid="footage-face-select" data-cast-count={(cast || []).length}
              data-other-open={otherOpen ? 'true' : 'false'} value={choice} disabled={saving}
              onChange={e => { const v = e.target.value; if (v === OTHER_CHOICE) { setOtherOpen(true); setChoice(''); } else setChoice(v); }}>
              <FaceChoiceOptions cast={cast} chars={chars} authored={f.authored} otherOpen={otherOpen} />
            </select>
            <Icon icon={ChevronDown} />
          </span>
          <span className="ui-cluster">
            <Button variant="secondary" data-testid="footage-face-save" data-state={saving ? 'saving' : st?.status === 'error' ? 'error' : 'idle'}
              disabled={saving || !choice} loading={saving && !st?.cleared && !st?.undoing} onClick={() => save(false)}>Save</Button>
            {f.authored && (
              <Button variant="quiet" data-testid="footage-face-clear" data-state={saving ? 'saving' : 'idle'} disabled={saving}
                loading={saving && st?.cleared && !st?.undoing} onClick={() => save(true)}>Clear</Button>
            )}
            {saving && st?.undoing && <span data-testid="footage-face-undoing" className="ui-meta" role="status">Undoing</span>}
            {st?.status === 'saved' && (
              <span data-testid="footage-face-indicator" data-kind={st.cleared ? 'cleared' : 'saved'} className="ui-cluster" role="status">
                <span className="ui-dense ui-text-2">{st.cleared ? 'Cleared' : 'Saved'}</span>
                {st.undoable && (
                  <Button variant="quiet" icon={Undo2} data-testid="footage-face-undo" onClick={() => sv.undo(f.tag, f.authored || null)}
                    title={`Undo: restore ${authoredLabel(st.prev)}${st.prev && st.prev.kind === 'crew' ? ' (crew)' : ''}`}>Undo</Button>
                )}
              </span>
            )}
          </span>
        </div>
      ) : <span className="ui-meta">An Admin or Producer names faces.</span>}
    </div>
  );
}
